import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { api } from "@/lib/api-client";
import {
  GetPaymentSettings,
  UpdateServicesPaymentSettings,
} from "@/server-types";
import { useForm } from "@mantine/form";
import { useMediaQuery } from "@mantine/hooks";
import { RiGlobalLine, RiSearch2Line } from "react-icons/ri";
import { StripePageHeader } from "./Components/StripePageHeader";
import { returnCurrencySymbol } from "../../../../../shared/utils/utils";
import { LimeInput } from "@/Components/NextBase/LimeInput";
import { Button, Checkbox, Divider, Tab, Tabs } from "@heroui/react";
import { cn } from "@/utils";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import { LimeNumberInput } from "@/Components/NextBase/LimeNumberInput";
import { LimeSwitch } from "@/Components/NextBase/LimeSwitch";

type PaymentTypes =
  UpdateServicesPaymentSettings["body"]["updateData"]["paymentType"];

export const ServicePaymentSettings = () => {
  const { mutateAsync: updateServicesPaymentSettings, isPending } =
    api.stripe.useUpdateStripePaymentSettingsServices();

  const { data: paymentSettings } = api.stripe.useGetStripePaymentSettings();

  const [selectedServiceIds, setSelectedServiceIds] = useState<number[]>([]);

  const [searchQuery, setSearchQuery] = useState("");

  const servicesPaymentForm = useForm({
    initialValues: {
      paymentAmount: 0,
      paymentType: "percentage" as PaymentTypes,
      formPaymentRequired: false,
    },
    validate: {
      paymentAmount: (value) => {
        if (!servicesPaymentForm.values.formPaymentRequired) return null;

        if (
          servicesPaymentForm.values.paymentType === "percentage" &&
          (value > 100 || value < 0)
        ) {
          return t`Vrednost mora biti med 0 in 100%`;
        }

        if (servicesPaymentForm.values.paymentType === "amount" && value < 0) {
          return t`Cena ne sme biti negativna`;
        }

        if (servicesPaymentForm.values.paymentType === "amount" && !value) {
          return t`Prosimo vnesite ceno`;
        }
      },

      paymentType: (value) => {
        if (!servicesPaymentForm.values.formPaymentRequired) return null;

        if (value !== "percentage" && value !== "amount") {
          return t`Vrednost mora biti odstotek ali znesek`;
        }
      },
    },
  });

  const closeEditModal = () => {
    setSelectedServiceIds([]);

    const data = {
      paymentAmount: 0,
      paymentType: "percentage" as PaymentTypes,
      formPaymentRequired: false,
    };
    servicesPaymentForm.setValues(data);
    servicesPaymentForm.resetDirty(data);
  };

  const handleServiceToggleChange = (
    service: GetPaymentSettings["response"]["services"][number],
  ) => {
    setSelectedServiceIds((ids) => {
      if (ids.includes(service.serviceId)) {
        const newIds = [...ids];
        newIds.splice(ids.indexOf(service.serviceId), 1);

        // reset form when closing edit modal
        if (newIds.length === 0) {
          const data = {
            paymentAmount: 0,
            paymentType: "percentage" as PaymentTypes,
            formPaymentRequired: false,
          };
          servicesPaymentForm.setValues(data);
          servicesPaymentForm.resetDirty(data);
        }

        return newIds;
      }

      if (ids.length === 0) {
        const takeGlobalSettings =
          service.formPaymentRequired &&
          service.paymentAmount === 0 &&
          paymentSettings?.global.paymentPriceAmount !== 0;

        const amount = takeGlobalSettings
          ? (paymentSettings?.global.paymentPriceAmount ?? 0)
          : (service.paymentAmount ?? 0);

        const type = takeGlobalSettings
          ? (paymentSettings?.global.paymentPriceType ?? "amount")
          : (service.paymentType ?? ("percentage" as PaymentTypes));

        const data = {
          paymentAmount: amount,
          paymentType: type,
          formPaymentRequired: service.formPaymentRequired ?? false,
        };
        servicesPaymentForm.setValues(data);
        servicesPaymentForm.resetDirty(data);
      }

      return [...ids, service.serviceId];
    });
  };

  const filteredServices = paymentSettings?.services.filter((service) => {
    return service.name?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const isMobile = useMediaQuery("(max-width: 768px)");

  return (
    <>
      <StripePageHeader
        title={t`Nastavitve po meri - storitve`}
        subtitle={t`Določite znesek za vsako storitev po meri.`}
        rightSection={
          isMobile ? null : (
            <LimeInput
              startContent={<RiSearch2Line />}
              placeholder={t`Išči storitve`}
              value={searchQuery}
              className="w-1/2 max-w-80"
              onChange={(event) => setSearchQuery(event.currentTarget.value)}
            />
          )
        }
      />

      {isMobile && (
        <>
          <LimeInput
            startContent={<RiSearch2Line />}
            placeholder={t`Išči storitve`}
            className="w-full"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.currentTarget.value)}
          />

          <Divider className="my-4" />
        </>
      )}

      <div className="flex w-full flex-col gap-2">
        {filteredServices?.map((service, _) => {
          const isSelected = selectedServiceIds.includes(service.serviceId);

          const rightSection = (() => {
            if (
              service.formPaymentRequired &&
              service.paymentAmount != null &&
              service.paymentAmount > 0
            ) {
              return (
                <p className="text-sm text-gray-500">
                  {service.paymentAmount}{" "}
                  {service.paymentType === "percentage"
                    ? "%"
                    : returnCurrencySymbol({
                        currency: service.currency,
                      })}
                </p>
              );
            }

            return (
              <div className="flex items-center justify-center text-center text-sm text-gray-500">
                {service.formPaymentRequired &&
                paymentSettings?.global.paymentPriceAmount !== 0 ? (
                  <>
                    {paymentSettings?.global.paymentPriceAmount}{" "}
                    {paymentSettings?.global.paymentPriceType === "percentage"
                      ? "%"
                      : returnCurrencySymbol({
                          currency: service.currency,
                        })}
                    <div className="absolute right-1 top-1">
                      <RiGlobalLine size={".8rem"} />
                    </div>
                  </>
                ) : (
                  <>/</>
                )}
              </div>
            );
          })();

          return (
            <>
              <button
                className={cn(
                  "flex h-12 items-center justify-start rounded-lg border-1 border-gray-300 py-1 pl-4 pr-1",
                  {
                    "border-[#548f68] bg-[#e6f5e8]": isSelected,
                  },
                )}
                onClick={() => {
                  handleServiceToggleChange(service);
                }}
              >
                <div className="flex w-full items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      isSelected={isSelected}
                      className="pointer-events-none"
                      onValueChange={() => handleServiceToggleChange(service)}
                    />

                    <p className="text-sm">{service.name}</p>
                  </div>
                  <div
                    className={cn(
                      "relative flex h-10 w-20 items-center justify-center rounded-md bg-[#F5F5F5] text-[#8C8C8C]",
                      {
                        "border-1 border-[#d8d8d8]": isSelected,
                      },
                    )}
                  >
                    {rightSection}
                  </div>
                </div>
              </button>
            </>
          );
        })}
      </div>

      <AnimatePresence>
        {selectedServiceIds.length > 0 && (
          <motion.div
            className="fixed bottom-0 left-0 right-0 z-50 h-fit border-t-1 border-gray-300 bg-white px-4 py-4 shadow-lg"
            initial={{ opacity: 0, y: "100%" }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: "100%" }}
            transition={{
              type: "spring",
              stiffness: 300, // spring stiffness
              damping: 25, // how quickly it comes to rest
              bounce: 0.2, // 0 = no bounce, 1 = super bouncy
            }}
          >
            <form
              onSubmit={servicesPaymentForm.onSubmit((values) => {
                updateServicesPaymentSettings({
                  serviceIds: selectedServiceIds,
                  updateData: values,
                }).then(() => closeEditModal());
              })}
            >
              <div className="flex w-full flex-col-reverse items-center justify-between gap-2 md:flex-row">
                <div className="w-full md:w-auto">
                  <LimeSwitch
                    title={t`Omogoči spletno plačevanje`}
                    classNames={{
                      base: "mr-2",
                    }}
                    isCompact
                    {...servicesPaymentForm.getInputProps(
                      "formPaymentRequired",
                      {
                        type: "checkbox",
                      },
                    )}
                  ></LimeSwitch>
                </div>
                <div className="flex w-full items-end gap-2 md:w-auto md:items-center">
                  <div className="flex flex-col items-center gap-2 md:flex-row">
                    <p className="text-sm text-gray-400">
                      <Trans>Izberite odstotke ali znesek</Trans>:{" "}
                    </p>
                    <LimeNumberInput
                      {...servicesPaymentForm.getInputProps("paymentAmount")}
                      className="w-36"
                      size="sm"
                      isDisabled={
                        !servicesPaymentForm.values.formPaymentRequired
                      }
                    />
                  </div>

                  <Tabs
                    selectedKey={servicesPaymentForm.values.paymentType}
                    onSelectionChange={(key) => {
                      servicesPaymentForm.setFieldValue(
                        "paymentType",
                        key as "percentage" | "amount",
                      );
                    }}
                    classNames={{
                      tab: "h-10",
                    }}
                    isDisabled={!servicesPaymentForm.values.formPaymentRequired}
                  >
                    <Tab key="percentage" title={"%"} />
                    <Tab
                      key="amount"
                      title={returnCurrencySymbol({
                        currency: paymentSettings?.mainCurrency ?? "EUR",
                      })}
                    />
                  </Tabs>
                </div>
              </div>
              <div className="mt-2 flex justify-end gap-2">
                <Button
                  onPress={() => closeEditModal()}
                  isLoading={isPending}
                  variant="bordered"
                  className="w-full md:w-24"
                >
                  <Trans>Prekliči</Trans>
                </Button>
                <Button
                  className="w-full md:w-24"
                  type="submit"
                  isLoading={isPending}
                  color="primary"
                >
                  <Trans>Shrani</Trans>
                </Button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
